import React, { useState, useEffect, useRef } from 'react';
import { useActiveCareerFocus } from '../../hooks/useCareerFocus';
import { useCareerFocusFeatures } from '../../hooks/useStatsigFeatures';
import { useDarkMode } from '../../context/DarkModeContext';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createLogger } from '../../utils/logger';
import { supabase } from '../../utils/supabase';
import StandardFocusHeader from '../Focus/StandardFocusHeader';
import { PlusIcon } from '@heroicons/react/24/outline';
import PerformanceTimeline from './PerformanceTimeline';
import QuarterlyPriorities from './QuarterlyPriorities/PrioritiesPanel';
import TabContainer from './TabContainer';
import MetricsOverview from './MetricsOverview';
import CompetencyCoverage from './CompetencyCoverage';
import QuickActions from './QuickActions';
import { useGoals } from '../../hooks/useGoals';
import { useCareerCards } from '../../hooks/useCareerCards';
import { usePerformanceReview } from '../../hooks/usePerformanceReview';
import { usePerformancePhase } from '../../hooks/usePerformancePhase';
import EnhancedQuickAddModal from '../QuickNote/EnhancedQuickAddModal';
import { PhaseGuidanceProvider } from '../../context/PhaseGuidanceContext';
import MultipleFocusWarning from '../Focus/MultipleFocusWarning';
import ReviewPreparationFlow from './ReviewPrep/ReviewPreparationFlow';
import STARBuilder from './Achievements/STARBuilder';
import { useAchievements } from '../../hooks/useAchievements';
import MetricsUpdateModal from './Metrics/MetricsUpdateModal';
import { useDefaultFocus } from '../../hooks/useDefaultFocus';
import TourOverlay, { TourStep } from '../shared/TourOverlay';
import { useDashboardTourStatus } from '../../hooks/useDashboardTourStatus';

const logger = createLogger({ module: 'PerformanceReviewDashboard' });

export type PerformancePhase = 'planning' | 'foundation' | 'execution' | 'impact' | 'review_prep';

const PerformanceReviewDashboard: React.FC = () => {
  const { focusId } = useParams<{ focusId?: string }>();
  const { focus, loading: focusLoading, error: focusError } = useActiveCareerFocus('performance_review', focusId);
  const { features } = useCareerFocusFeatures();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const { markDashboardTourCompleted } = useDashboardTourStatus();
  const [activeTab, setActiveTab] = useState<'goals' | 'competencies' | 'achievements' | 'metrics' | 'reviews'>('goals');
  const [quickAddContext, setQuickAddContext] = useState<{
    title?: string;
    placeholder?: string;
    defaultImpact?: number;
    defaultEngagement?: number;
  } | undefined>();
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [showReviewPrep, setShowReviewPrep] = useState(false);
  const [showSTARBuilder, setShowSTARBuilder] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [isCondensed, setIsCondensed] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  
  // Check if this is the default focus
  const { defaultFocusId } = useDefaultFocus();
  const isDefaultFocus = defaultFocusId === focus?.id;

  // Fetch performance review specific data
  const { 
    data: performanceData,
    loading: performanceLoading,
    error: performanceError,
    refreshData: refreshPerformance,
    competencies,
    metrics,
    settings
  } = usePerformanceReview(focus?.id);
  
  // Fetch goals
  const { 
    goals, 
    loading: goalsLoading, 
    error: goalsError,
    refreshGoals 
  } = useGoals(focus?.id || null);
  
  // Fetch career cards
  const { 
    cards, 
    isLoading: cardsLoading,
    error: cardsError,
    refetch: refreshCards 
  } = useCareerCards(); // Get all cards, filtering can be done by date in the UI

  // Use performance phase calculation
  const phaseData = usePerformancePhase(focus);
  const { currentPhase, monthsElapsed, totalMonths } = phaseData;
  const progressPercentage = (monthsElapsed / totalMonths) * 100;
  
  // Use achievements hook for STAR Builder
  const {
    createAchievement,
    updateAchievement,
    refreshAchievements
  } = useAchievements(focus?.id);

  // Handle scroll detection for timeline condensing
  useEffect(() => {
    if (!focus) return;
    
    const scrollContainer = scrollContainerRef.current;
    const timeline = timelineRef.current;
    
    if (!scrollContainer || !timeline) return;

    const handleScroll = () => {
      const timelineRect = timeline.getBoundingClientRect();
      const headerHeight = 64; // Height of the fixed header
      
      // Show condensed view when timeline is scrolled out of view
      setIsCondensed(timelineRect.bottom < headerHeight);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [focus]);

  const bgColor = darkMode ? 'bg-gray-900' : 'bg-gray-50';

  const handleQuickAdd = (context?: any) => {
    setQuickAddContext(context);
    setShowQuickAddModal(true);
  };

  const handleRefreshData = async () => {
    await Promise.all([
      refreshGoals(),
      refreshCards(),
      refreshPerformance()
    ]);
  };

  const getPhaseDisplay = (phase: string) => {
    const phaseMap: Record<string, string> = {
      planning: 'Planning',
      foundation: 'Foundation',
      execution: 'Execution',
      impact: 'Impact',
      review_prep: 'Review Prep'
    };
    return phaseMap[phase] || phase;
  };

  // Define tour steps
  const tourSteps: TourStep[] = [
    {
      title: "Welcome to Your Performance Review Dashboard! 👋",
      content: "This dashboard helps you track your performance metrics, build evidence for your achievements, and prepare for performance reviews. Let's explore the key features!",
      position: "center"
    },
    {
      element: ".performance-timeline",
      title: "Review Cycle Timeline",
      content: `You're in Month ${monthsElapsed} of ${totalMonths} - currently in the ${getPhaseDisplay(currentPhase)} phase. This timeline shows your progress through the review cycle and upcoming milestones.`,
      position: "bottom"
    },
    {
      element: ".quarterly-priorities",
      title: "Quarterly Priorities",
      content: "Track your top priorities for each quarter. These help you stay focused on what matters most and align with your team's objectives.",
      position: "bottom"
    },
    {
      element: ".quick-add-btn",
      title: "Quick Add Button",
      content: "Document your wins, learnings, and feedback regularly. This is your most important tool for building a strong case during performance reviews!",
      position: "left"
    },
    {
      element: ".tabs-container",
      title: "Main Content Tabs",
      content: "Navigate between Goals (personal objectives), Competencies (skills to develop), Achievements (your wins), Metrics (measurable impact), and Reviews (past evaluations).",
      position: "top"
    },
    {
      element: ".metrics-overview",
      title: "Key Metrics",
      content: "Track quantifiable metrics that demonstrate your impact. Update these regularly to show continuous improvement and growth.",
      position: "left"
    },
    {
      element: ".competency-coverage",
      title: "Competency Coverage",
      content: "See which competencies you've documented evidence for. Green means you have evidence, gray means you need more examples.",
      position: "left"
    },
    {
      element: ".quick-actions",
      title: "Quick Actions",
      content: "Access common tasks like adding metrics, creating STAR achievements, recording feedback, and preparing for your next review.",
      position: "left"
    },
    {
      element: ".focus-mode-slider",
      title: "Focus Mode Toggle",
      content: "Switch between your regular dashboard and this focused performance review view to concentrate on review-specific goals.",
      position: "bottom"
    }
  ];

  // Show loading state
  if (focusLoading || performanceLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Show error state
  if (focusError || performanceError || !focus) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">Unable to load performance review dashboard</p>
          <button 
            onClick={() => navigate('/focus')}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
          >
            Return to Focus Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <PhaseGuidanceProvider industryType="general">
      <div className={`min-h-screen ${bgColor} flex flex-col`}>
        {/* Fixed Header with Condensed Timeline */}
        <StandardFocusHeader
          focus={focus}
          mobileTitle="Perf Review"
          actionButton={{
            label: 'Quick Add',
            icon: <PlusIcon className="w-4 h-4" />,
            onClick: handleQuickAdd,
            variant: 'primary'
          }}
          isCondensed={isCondensed}
          isDefaultFocus={isDefaultFocus}
          onStartTour={() => {
            // Small delay to ensure all elements are rendered
            setTimeout(() => setShowTour(true), 100);
          }}
          condensedContent={
            <div className="px-3 sm:px-6 py-2">
              <div className="relative group">
                {/* Condensed Progress Bar */}
                <div className={`h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                  <motion.div 
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                    animate={{ width: `${Math.min(progressPercentage, 100)}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
                
                {/* Hover tooltip */}
                <div className="absolute left-1/2 -translate-x-1/2 -top-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-800 text-white text-xs px-2 py-1 rounded pointer-events-none whitespace-nowrap">
                  Month {monthsElapsed} of {totalMonths} • {getPhaseDisplay(currentPhase)} Phase • {Math.round(progressPercentage)}%
                </div>
              </div>
            </div>
          }
        />

        {/* Scrollable Content */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto"
        >
          {/* Multiple Focus Warning */}
          {focus && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
              <MultipleFocusWarning 
                focusType={focus.focus_type} 
                currentFocusId={focus.id} 
              />
            </div>
          )}
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Main Content (spans 2 columns) */}
              <div className="lg:col-span-2 space-y-6">
                {/* Timeline - Part of scrollable content */}
                <div ref={timelineRef} className="performance-timeline">
                  <PerformanceTimeline
                    focus={focus}
                    currentPhase={currentPhase as PerformancePhase}
                    monthsElapsed={monthsElapsed}
                    totalMonths={totalMonths}
                  />
                </div>

                {/* Quarterly Priorities */}
                <div className="quarterly-priorities">
                  <QuarterlyPriorities
                  focusId={focus.id}
                  focus={focus}
                  currentPhase={currentPhase}
                  onQuickAdd={handleQuickAdd}
                  />
                </div>

                {/* Main Content Tabs */}
                <div className="tabs-container">
                  <TabContainer
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  focus={focus}
                  goals={goals}
                  competencies={competencies}
                  cards={cards}
                  onRefresh={handleRefreshData}
                  onQuickAdd={handleQuickAdd}
                  onPrepareReview={() => setShowReviewPrep(true)}
                  />
                </div>
              </div>

              {/* Right Column - Sidebar */}
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="metrics-overview">
                  <MetricsOverview
                  metrics={metrics}
                  focusId={focus.id}
                  onRefresh={refreshPerformance}
                  compact={true}
                  />
                </div>

                {/* Competency Coverage */}
                <div className="competency-coverage">
                  <CompetencyCoverage
                  competencies={competencies}
                  cards={cards}
                  focusId={focus.id}
                  onAddEvidence={() => handleQuickAdd({
                    title: 'Add Evidence',
                    placeholder: 'Describe the evidence for your competencies...',
                    defaultImpact: 4,
                    defaultEngagement: 4
                  })}
                  />
                </div>

                {/* Quick Actions */}
                <div className="quick-actions">
                  <QuickActions
                  onAddMetric={() => setShowMetricsModal(true)}
                  onAddAchievement={() => setShowSTARBuilder(true)}
                  onRecordFeedback={() => handleQuickAdd({
                    title: 'Record Feedback',
                    placeholder: 'What feedback did you receive?',
                    defaultImpact: 3,
                    defaultEngagement: 3
                  })}
                  onCreateCard={() => handleQuickAdd()}
                  onPrepareReview={() => setShowReviewPrep(true)}
                  focus={focus}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Add Modal */}
        <AnimatePresence>
          {showQuickAddModal && (
            <EnhancedQuickAddModal
              isOpen={showQuickAddModal}
              onClose={() => {
                setShowQuickAddModal(false);
                setQuickAddContext(undefined);
              }}
              context={quickAddContext}
              onSuccess={() => {
                setShowQuickAddModal(false);
                setQuickAddContext(undefined);
                refreshCards();
              }}
            />
          )}
        </AnimatePresence>

        {/* Review Preparation Flow */}
        <ReviewPreparationFlow
          focusId={focus.id}
          isOpen={showReviewPrep}
          onClose={() => setShowReviewPrep(false)}
          onComplete={() => {
            setShowReviewPrep(false);
            handleRefreshData();
            // Switch to Reviews tab to show the new review
            setActiveTab('reviews');
          }}
        />

        {/* STAR Builder Modal */}
        <STARBuilder
          isOpen={showSTARBuilder}
          focusId={focus.id}
          onClose={() => setShowSTARBuilder(false)}
          onSave={async (achievement) => {
            await createAchievement(achievement);
            setShowSTARBuilder(false);
            handleRefreshData();
            // Switch to achievements tab to show the new achievement
            setActiveTab('achievements');
          }}
        />

        {/* Metrics Modal */}
        <MetricsUpdateModal
          isOpen={showMetricsModal}
          onClose={() => setShowMetricsModal(false)}
          onSave={async (metricData) => {
            // Save the metric to the database
            const { error } = await supabase
              .from('review_metrics')
              .insert({
                focus_id: focus.id,
                metric_name: metricData.metric_name,
                metric_value: metricData.current_value,
                current_value: metricData.current_value,
                baseline_value: metricData.baseline_value,
                target_value: metricData.target_value,
                metric_unit: metricData.metric_unit,
                category: metricData.category,
                notes: metricData.notes,
                metric_date: new Date().toISOString()
              });
            
            if (error) {
              console.error('Error saving metric:', error);
              throw error;
            }
            
            await refreshPerformance();
            setShowMetricsModal(false);
            // Switch to metrics tab to show the new metric
            setActiveTab('metrics');
          }}
        />

        {/* Tour Overlay */}
        {showTour && (
          <TourOverlay
            isOpen={showTour}
            onClose={() => setShowTour(false)}
            steps={tourSteps}
            onComplete={() => {
              setShowTour(false);
              markDashboardTourCompleted();
            }}
          />
        )}
      </div>
    </PhaseGuidanceProvider>
  );
};

export default PerformanceReviewDashboard;